import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Card, Table, Switch, Input, Select, Space, Typography, Tag, Tooltip, Button, Row, Col, Alert, Form } from 'antd';
import stringify from 'fast-json-stable-stringify';
import { SettingOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Editor } from '@monaco-editor/react';
import { EndpointMapping, TemplateDynamicFields } from '../types/index';
import { useTheme } from '../contexts/ThemeContext';
import {
  validatePathSyntax,
  isPathReserved,
  inflateFromApi,
  clearOfferFields,
} from "@/helpers/helpers.app";
import { sanitizeForSave } from "@/helpers/endpoint-mapping-sanitize";
import { findApiVsStaticConflicts } from '@/utils/appUtils'; 

const { Text } = Typography;
const { Option } = Select;

interface EndpointMappingsProps {
  templateDynamicFields?: TemplateDynamicFields;
  value?: EndpointMapping[];
  initialMappingsFromDb?: EndpointMapping[];
  onChange?: (mappings: EndpointMapping[]) => void;
  staticSites?: Array<{
    id: string;
    siteName?: string;
    template?: { name?: string | null } | null;
    subdomain?: string | null;
    domain?: string | null;
    deploymentUrl?: string | null;
    status?: string | null;
  }>;
  isEditMode?: boolean;
  dynamicFields?: Array<{ name: string, value: string }>; // Для перевірки наявності ENCRYPTION_KEY
  form?: any; // Form instance для читання значень
  editingApp?: { dynamicFields?: any }; // Дані з бази для fallback
}

/** Імперативний handle для прямого оновлення списку (наприклад після sync на продакшені). */
export interface EndpointMappingsHandle {
  applySync(raw: EndpointMapping[]): void;
}

export const EndpointMappingsComponent = forwardRef<EndpointMappingsHandle, EndpointMappingsProps>(({
  templateDynamicFields,
  value,
  initialMappingsFromDb,
  onChange,
  staticSites = [],
  isEditMode,
  dynamicFields = [],
  form,
  editingApp
}, ref) => {
  const { monacoTheme } = useTheme();
  const [mappings, setMappings] = useState<EndpointMapping[]>([]);
  const [focusedField, setFocusedField] = useState<{ index: number, field: string } | null>(null);
  const inputRefs = useRef<{ [key: string]: any }>({});
  const caretRef = useRef<Record<string, { start: number; end: number }>>({});
  const fromPropsRef = useRef(false);
  const initializedRef = useRef(false);
  const prevSerializedRef = useRef<string>("");
  const prevTemplateEndpointsRef = useRef<string>("");
  // Локальний стан для Monaco Editor - оновлюється тільки при втраті фокусу
  const monacoLocalValues = useRef<Record<string, string>>({});
  const monacoEditorRefs = useRef<Record<string, any>>({});

  const effectiveValue = useMemo(
    () => (value && value.length > 0) ? value : (initialMappingsFromDb && initialMappingsFromDb.length > 0 ? initialMappingsFromDb : value),
    [value, initialMappingsFromDb]
  );

  useImperativeHandle(ref, () => ({
    applySync(raw: EndpointMapping[]) {
      if (!Array.isArray(raw)) return;
      prevSerializedRef.current = stringify(raw);
      initializedRef.current = true;
      fromPropsRef.current = true;
      setMappings(raw.map(inflateFromApi));
    },
  }), []);

  useEffect(() => {
    if (!Array.isArray(effectiveValue)) return;
    const serialized = stringify(effectiveValue);
    const serializedEqualPrev = serialized === prevSerializedRef.current;
    if (serializedEqualPrev) {
      return; // нет реального изменения
    }

    prevSerializedRef.current = serialized;
    fromPropsRef.current = true;
    if (effectiveValue.length > 0) {
      initializedRef.current = true;
      setMappings(effectiveValue.map(inflateFromApi));
    } else {
      initializedRef.current = false;
    }
  }, [effectiveValue]);


  useEffect(() => {
    // Перевіряємо чи змінився шаблон - якщо так, скидаємо initialized
    const currentTemplateEndpoints = templateDynamicFields?.endpoints
      ? stringify(templateDynamicFields.endpoints.map(e => e.name))
      : "";
    if (currentTemplateEndpoints !== prevTemplateEndpointsRef.current && currentTemplateEndpoints) {
      initializedRef.current = false;
      prevTemplateEndpointsRef.current = currentTemplateEndpoints;
    }

    const valueSerialized = effectiveValue ? stringify(effectiveValue) : "";
    const shouldUpdate = !initializedRef.current || (effectiveValue && effectiveValue.length > 0 && valueSerialized !== prevSerializedRef.current);
    const didReturnEarly = initializedRef.current && effectiveValue && effectiveValue.length > 0 && !shouldUpdate;
    if (didReturnEarly) {
      return;
    }

    if (effectiveValue && effectiveValue.length > 0) {
      initializedRef.current = true;
      prevSerializedRef.current = valueSerialized;
      setMappings(effectiveValue.map(inflateFromApi));
      return;
    }

    if (!templateDynamicFields?.endpoints || templateDynamicFields.endpoints.length === 0) {
      setMappings([]);
      initializedRef.current = true;
      return;
    }

    if (isEditMode) {
      return;
    }

    if ((!effectiveValue || effectiveValue.length === 0) && !initializedRef.current) {
      const defaultMappings: EndpointMapping[] = templateDynamicFields.endpoints.map(endpoint => {
        const existingRaw = effectiveValue?.find?.(m => m.handler === endpoint.name);
        // Обробляємо через inflateFromApi, щоб витягти errorStaticSiteId з offerError якщо потрібно
        const existing = existingRaw ? inflateFromApi(existingRaw) : null;
        const isOffer = endpoint.isOffer || false;
        const hasOfferMacros = checkHasOfferMacros(endpoint.name); // legacy check
        const isWebSocket = endpoint.name === "handleWebSocket" || endpoint.name === "handleGet";
        const responseType =
          existing?.offerResponseType ||
          (isWebSocket ? "websocket" : isOffer ? "redirect" : "json");

        const finalIsOffer = existing?.isOffer !== undefined ? existing.isOffer : isOffer;

        const defaultOfferUrl = finalIsOffer
          ? responseType === "websocket"
            ? JSON.stringify({ success: true, url: "{{OFFER_URL}}" })
            : responseType === "redirect"
              ? "{{OFFER_URL}}"
              : JSON.stringify({ success: true, redirect: "{{OFFER_URL}}" })
          : undefined;

        const defaultOfferError = finalIsOffer
          ? (existing?.offerError ||
            (responseType === "websocket"
              ? JSON.stringify({ success: false, url: "" })
              : JSON.stringify({ error: true, message: "{{OFFER_ERROR}}" })))
          : undefined;

        const baseMapping = {
          handler: endpoint.name,
          path: existing?.path || endpoint.path,
          method: existing?.method || endpoint.method,
          enabled:
            existing?.enabled !== undefined
              ? existing.enabled
              : endpoint.enabled, // використовуємо enabled з шаблону
          subdomain: existing?.subdomain || "",
          isOffer: finalIsOffer,
          // Legacy fields (не включаємо hasOfferMacros в DTO)
          responseType:
            existing?.responseType || (hasOfferMacros ? "JSON" : undefined),
        };

        // Offer-поля тільки для оферних функцій
        if (finalIsOffer) {
          return {
            ...baseMapping,
            // Offer response settings
            offerResponseType: existing?.offerResponseType || responseType,
            offerResponseCode: existing?.offerResponseCode || 200,
            offerUrl: existing?.offerUrl ?? defaultOfferUrl,
            // Offer error settings
            offerErrorType: existing?.offerErrorType || "json",
            offerErrorCode: existing?.offerErrorCode || 400,
            offerError: existing?.offerError ?? defaultOfferError,
            // Landing parameters for the error
            errorLandingSource: existing?.errorLandingSource || "staticSite",
            errorStaticSiteId: existing?.errorStaticSiteId || "",
            errorLandingIndexPath:
              existing?.errorLandingIndexPath || "index.html",
          };
        } else {
          // Для не оферних функцій встановлюємо undefined
          return {
            ...baseMapping,
            offerResponseType: undefined,
            offerResponseCode: undefined,
            offerUrl: undefined,
            offerErrorType: undefined,
            offerErrorCode: undefined,
            offerError: undefined,
            errorLandingSource: undefined,
            errorStaticSiteId: undefined,
            errorLandingIndexPath: undefined,
          };
        }
      });

      setMappings(defaultMappings);
      initializedRef.current = true;
    }
  }, [templateDynamicFields?.endpoints, effectiveValue, isEditMode]);

  // Відновлюємо фокус після оновлення стану
  useEffect(() => {
    if (focusedField) {
      const key = `${focusedField.index}-${focusedField.field}`;
      const inputRef = inputRefs.current[key];
      if (!inputRef.focus) return

      requestAnimationFrame(() => {
        inputRef.focus()

        const el = inputRef.input as HTMLInputElement | undefined
        if (!el) return
        const saved = caretRef.current[key]
        if (saved) {
          el.setSelectionRange(saved.start, saved.end)
        } else {
          const pos = el.selectionEnd ?? el.value.length
          el.setSelectionRange(pos, pos)
        }
      })
    }
  }, [mappings, focusedField]);

  // Синхронізуємо локальні значення Monaco Editor з основним станом при зовнішніх змінах
  useEffect(() => {
    if (fromPropsRef.current) {
      // Оновлюємо локальні значення тільки якщо зміни прийшли ззовні
      mappings.forEach((mapping, index) => {
        const offerUrlKey = `offerUrl-${index}`;
        const offerErrorKey = `offerError-${index}`;
        if (mapping.offerUrl !== undefined) {
          monacoLocalValues.current[offerUrlKey] = mapping.offerUrl || "";
        }
        if (mapping.offerError !== undefined) {
          monacoLocalValues.current[offerErrorKey] = mapping.offerError || "";
        }
      });
    }
  }, [mappings]);


  const updateMapping = (index: number, field: keyof EndpointMapping, newValue: any) => {

    setMappings(prev => {
      const next = [...prev]
      const cur = { ...next[index], [field]: newValue }

      // Обробка зміни isOffer: очищаємо offer-поля для не оферних функцій
      if (field === "isOffer") {
        const wasOffer = next[index].isOffer;
        const isNowOffer = Boolean(newValue);

        if (!isNowOffer && wasOffer) {
          // Функція стала не оферною - очищаємо offer-поля
          Object.assign(cur, clearOfferFields(cur));
        } else if (isNowOffer && !wasOffer) {
          // Функція стала оферною - встановлюємо дефолтні значення
          cur.offerResponseType = "redirect";
          cur.offerResponseCode = 200;
          cur.offerUrl = "{{OFFER_URL}}";
          cur.offerErrorType = "json";
          cur.offerErrorCode = 400;
          cur.offerError = JSON.stringify(
            { error: true, message: "{{OFFER_ERROR}}" },
            null,
            2
          );
          cur.errorLandingSource = "staticSite";
          cur.errorStaticSiteId = "";
          cur.errorLandingIndexPath = "index.html";
        }
      }

      if (field === "offerResponseType") {
        // newValue может быть 'json', 'redirect', 'encrypted' або 'websocket'
        if (newValue === "websocket") {
          // WebSocket: фіксуємо метод GET та встановлюємо JSON-шаблони Gray/White результатів
          cur.method = "GET";
          cur.offerResponseCode = 101;
          cur.offerUrl = JSON.stringify(
            { id: "{{OFFER_URL}}", type: "event_feed", status: "active" },
            null,
            2
          );
          cur.offerErrorType = "json";
          cur.offerErrorCode = 400;
          cur.offerError = JSON.stringify(
            { id: "white", type: "event_feed", status: "inactive" },
            null,
            2
          );
        } else if (newValue === "json") {
          // поставить pretty JSON с макросом если поле пустое или сейчас строка-макрос
          try {
            const curVal = next[index].offerUrl;
            if (
              !curVal ||
              (typeof curVal === "string" && curVal.trim() === "")
            ) {
              cur.offerUrl = JSON.stringify(
                { success: true, redirect: "{{OFFER_URL}}" },
                null,
                2
              );
            } else {
              // если в поле уже plain string macro — конвертируем в pretty JSON
              if (typeof curVal === "string" && curVal.trim().startsWith("{")) {
                // возможно уже JSON — оставим
                cur.offerUrl = curVal;
              } else if (typeof curVal === "string") {
                cur.offerUrl = JSON.stringify(
                  { success: true, redirect: curVal },
                  null,
                  2
                );
              }
            }
          } catch {
            cur.offerUrl = JSON.stringify(
              { success: true, redirect: "{{OFFER_URL}}" },
              null,
              2
            );
          }
        } else if (newValue === "encrypted") {
          // Encrypted response: встановлюємо дефолтний JSON template
          try {
            const curVal = next[index].offerUrl;
            if (
              !curVal ||
              (typeof curVal === "string" && curVal.trim() === "")
            ) {
              cur.offerUrl = JSON.stringify(
                { url: "{{OFFER_URL}}", timestamp: "{{TIMESTAMP}}" },
                null,
                2
              );
            } else {
              // Якщо вже є JSON - залишаємо
              if (typeof curVal === "string" && curVal.trim().startsWith("{")) {
                cur.offerUrl = curVal;
              } else if (typeof curVal === "string") {
                // Якщо plain string - конвертуємо в JSON template
                cur.offerUrl = JSON.stringify(
                  { url: curVal, timestamp: "{{TIMESTAMP}}" },
                  null,
                  2
                );
              }
            }
            // Встановлюємо дефолтний алгоритм (завжди AES-256-CBC)
            cur.encryptionAlgorithm = "aes-256-cbc";
          } catch {
            cur.offerUrl = JSON.stringify(
              { url: "{{OFFER_URL}}", timestamp: "{{TIMESTAMP}}" },
              null,
              2
            );
            if (!cur.encryptionAlgorithm) {
              cur.encryptionAlgorithm = "aes-256-cbc";
            }
          }
        } else {
          // redirect -> plain macro string
          cur.offerUrl = "{{OFFER_URL}}";
        }
      }

      if (field === "offerErrorType") {
        if (newValue === "json") {
          if (
            !next[index].offerError ||
            (typeof next[index].offerError === "string" &&
              next[index].offerError.trim() === "")
          ) {
            cur.offerError = JSON.stringify(
              { error: true, message: "{{OFFER_ERROR}}" },
              null,
              2
            );
          } else {
            // try to preserve existing text if it looks like JSON
            cur.offerError = next[index].offerError;
          }
        } else if (newValue === "encrypted") {
          if (
            !next[index].offerError ||
            (typeof next[index].offerError === "string" &&
              next[index].offerError.trim() === "")
          ) {
            cur.offerError = JSON.stringify(
              { error: true, message: "{{OFFER_ERROR}}", timestamp: "{{TIMESTAMP}}" },
              null,
              2
            );
          } else {
            // try to preserve existing text if it looks like JSON
            cur.offerError = next[index].offerError;
          }
        } else if (newValue === "render") {
          // render mode -> clear offerError and keep landing fields
          cur.offerError = "";
          if (!cur.errorLandingIndexPath)
            cur.errorLandingIndexPath = "index.html";
        } else {
          // redirect -> plain macro
          cur.offerError = "https://example.com";
        }
      }
      next[index] = cur;
      return next
    })
  };



  useEffect(() => {
    if (fromPropsRef.current) {
      fromPropsRef.current = false;
      return;
    }
    if (!onChange) return;

    // Викликаємо onChange синхронно для критичних полів (errorStaticSiteId, offerErrorType)
    // щоб валідація в батьківському компоненті бачила актуальні дані
    // Використовуємо requestAnimationFrame для мінімізації ре-рендерів, але без затримки
    requestAnimationFrame(() => {
      onChange(sanitizeForSave(mappings));
    });
  }, [mappings, onChange]);

  // Перевірка наявності ENCRYPTION_KEY макросу в поточному шаблоні
  const encryptionKeyMacroExists = useMemo(() => {
    return templateDynamicFields?.dynamicFields?.some(
      (field) => field.name?.toUpperCase() === "ENCRYPTION_KEY"
    ) || false;
  }, [templateDynamicFields?.dynamicFields]);

  // Функція для отримання dynamicFields з бази даних
  const getDynamicFieldsFromDb = useMemo(() => {
    return (): Array<{ name: string, value: string }> => {
      if (!editingApp?.dynamicFields) return [];

      let appDynamicFields: any = editingApp.dynamicFields;
      if (typeof appDynamicFields === 'string') {
        try {
          appDynamicFields = JSON.parse(appDynamicFields);
        } catch {
          return [];
        }
      }

      if (Array.isArray(appDynamicFields)) {
        return appDynamicFields.map((field: any) => ({
          name: field.name || '',
          value: String(field.value || '')
        }));
      }

      return [];
    };
  }, [editingApp?.dynamicFields]);

  // Відстежуємо зміни dynamicFields в формі для real-time оновлень
  const formDynamicFieldsWatch = form ? (Form.useWatch("dynamicFields", form) || {}) : {};

  // Перевірка наявності ENCRYPTION_KEY значення
  // Важливо: перевіряємо тільки якщо макрос є в поточному шаблоні
  const hasEncryptionKey = useMemo(() => {
    // Якщо макросу немає в шаблоні - не показуємо опцію шифрування
    if (!encryptionKeyMacroExists) {
      return false;
    }

    // 1. Спочатку перевіряємо з форми (через useWatch проп)
    const fromFormWatch = dynamicFields?.some(
      (field) =>
        field.name?.toUpperCase() === "ENCRYPTION_KEY" &&
        field.value &&
        field.value.trim() !== ""
    );

    if (fromFormWatch) return true;

    // 2. Перевіряємо форму напряму (через Form.useWatch для real-time оновлень)
    if (formDynamicFieldsWatch && typeof formDynamicFieldsWatch === "object") {
      const encryptionKeyValue =
        formDynamicFieldsWatch["ENCRYPTION_KEY"] ||
        formDynamicFieldsWatch["encryption_key"] ||
        formDynamicFieldsWatch["Encryption_Key"];
      if (encryptionKeyValue && String(encryptionKeyValue).trim() !== "") {
        return true;
      }
    }

    // 3. Якщо немає в формі, читаємо з бази даних (тільки якщо макрос є в шаблоні)
    const dbFields = getDynamicFieldsFromDb();
    const fromDb = dbFields.some(
      (field) =>
        field.name?.toUpperCase() === "ENCRYPTION_KEY" &&
        field.value &&
        field.value.trim() !== ""
    );

    return fromDb || false;
  }, [dynamicFields, formDynamicFieldsWatch, encryptionKeyMacroExists, getDynamicFieldsFromDb]);

  const isDuplicatePath = (path: string, method: string, currentIndex: number): boolean => {
    return mappings.some((mapping, index) =>
      index !== currentIndex &&
      mapping.enabled &&
      mapping.path === path &&
      mapping.method === method
    );
  };

  const getPathValidationError = (
    path: string,
    method: string,
    index: number
  ): string | null => {
    if (!path) return null;

    let basePath = path.toString().trim();
    if (!basePath.startsWith("/")) basePath = `/${basePath}`;

    const syntaxErr = validatePathSyntax(basePath);
    if (syntaxErr) {

      return syntaxErr;
    }

    if (isPathReserved(basePath)) {
      return "Цей шлях є зарезервованим системним ендпоінтом";
    }

    if (isDuplicatePath(basePath, method, index)) {
      return "Ендпоінт з таким шляхом та методом вже існує";
    }

    const routes = (staticSites || []).map((s) => ({
      subdomain: (s as any).subdomain || "",
      basePath: (s as any).basePath || "/",
      enabled: (s as any).status !== "inactive",
    }));


    const mappingProbe = [
      {
        subdomain: mappings[index]?.subdomain || "",
        path: basePath,
        enabled: true,
        handler: mappings[index]?.handler || "",
      },
    ];

    const conflicts = findApiVsStaticConflicts(
      routes as any,
      mappingProbe as any
    );
    if (conflicts && conflicts.length > 0) {
      const first = conflicts[0];
      return `Конфлікт з лендінгом: ${first.sub ? `${first.sub}.` : ""}${""
        }${first.path}${first.api ? ` (API: ${first.api})` : ""}`;
    }

    return null;
  };

  const checkHasOfferMacros = (handlerName: string): boolean => {
    // Пошук функції у коді шаблону та перевірка чи використовує OFFER_URL/OFFER_ERROR
    // Поки що простий check за назвою функції
    const functionNamePatterns = [
      'webhook', 'click', 'conversion', 'postback', 'redirect', 'response'
    ];
    return functionNamePatterns.some(pattern =>
      handlerName.toLowerCase().includes(pattern)
    );
  };

  const generateRandomUrl = (type: 'offer' | 'error'): string => {
    const domains = ['tracker.example.com', 'offers.partner.com', 'analytics.network.com'];
    const paths = type === 'offer'
      ? ['success', 'complete', 'convert', 'offer', 'landing']
      : ['error', 'fail', 'redirect', 'fallback', 'block'];

    const domain = domains[Math.floor(Math.random() * domains.length)];
    const path = paths[Math.floor(Math.random() * paths.length)];
    const param = Math.random().toString(36).substring(2, 8);

    return `https://${domain}/${path}?id=${param}`;
  };

  const getTemplateEndpoint = (handler: string) => {
    return templateDynamicFields?.endpoints?.find?.(e => e.name === handler);
  };

  const columns = [
    {
      title: 'Обробник',
      dataIndex: 'handler',
      key: 'handler',
      render: (handler: string) => {
        const templateEndpoint = getTemplateEndpoint(handler);
        return (
          <Space>
            <code>{handler}</code>
            {templateEndpoint?.description && (
              <Tooltip title={templateEndpoint.description}>
                <InfoCircleOutlined style={{ color: '#1890ff' }} />
              </Tooltip>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Метод',
      dataIndex: 'method',
      key: 'method',
      width: 120,
      render: (method: string, record: EndpointMapping, index: number) => (
        <Select
          value={method}
          onChange={(value) => updateMapping(index, 'method', value)}
          style={{ width: '100%' }}
          disabled={!record.enabled || record.offerResponseType === "websocket"}
        >
          <Option value="GET">GET</Option>
          <Option value="POST">POST</Option>
          <Option value="PUT">PUT</Option>
          <Option value="DELETE">DELETE</Option>
          <Option value="PATCH">PATCH</Option>
        </Select>
      )
    },
    {
      title: 'Шлях',
      dataIndex: 'path',
      key: 'path',
      render: (path: string, record: EndpointMapping, index: number) => {
        const validationError = getPathValidationError(path, record.method, index);
        const hasError = !!validationError;
        const inputKey = `${index}-path`;

        return (
          <Input
            ref={(ref) => {
              if (ref) {
                inputRefs.current[inputKey] = ref;
              }
            }}
            value={path}
            onChange={(e) => {
              const el = e.target as HTMLInputElement

              caretRef.current[inputKey] = {
                start: el.selectionStart ?? el.value.length,
                end: el.selectionEnd ?? el.value.length
              }

              updateMapping(index, 'path', el.value)
              setFocusedField({ index, field: "path" })
            }}
            onFocus={() => {
              setFocusedField({ index, field: 'path' });
            }}
            onBlur={(e) => {
              let v = (e.target as HTMLInputElement).value || '';
              v = v.trim()
              if (v === '') {
                v = '/'
              } else if (!v.startsWith('/')) {
                v = '/' + v
              }
              updateMapping(index, 'path', v)
              setFocusedField(null);
              delete caretRef.current[inputKey]
            }}
            placeholder={record.offerResponseType === "websocket" ? "/ws/v1/stream" : "/webhook"}
            disabled={!record.enabled}
            status={hasError ? 'error' : undefined}
            suffix={
              hasError ? (
                <Tooltip title={validationError}>
                  <InfoCircleOutlined style={{ color: '#ff4d4f' }} />
                </Tooltip>
              ) : null
            }
          />
        );
      }
    },
    {
      title: 'Сабдомен',
      dataIndex: 'subdomain',
      key: 'subdomain',
      width: 120,
      render: (subdomain: string, record: EndpointMapping, index: number) => {
        const inputKey = `${index}-subdomain`;

        return (
          <Input
            ref={(ref) => {
              if (ref) {
                inputRefs.current[inputKey] = ref;
              }
            }}
            value={subdomain || ''}
            onChange={(e) => updateMapping(index, 'subdomain', e.target.value)}
            onFocus={() => {
              setFocusedField({ index, field: 'subdomain' });
            }}
            onBlur={() => {
              setFocusedField(null);
            }}
            placeholder="api"
            disabled={!record.enabled}
            size="small"
          />
        );
      }
    },
    {
      title: 'Статус',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean, record: EndpointMapping, index: number) => {
        const templateEndpoint = getTemplateEndpoint(record.handler);
        const isRequired = templateEndpoint?.required;

        return (
          <Space direction="vertical" align="center">
            <Switch
              checked={enabled}
              onChange={(checked) => updateMapping(index, 'enabled', checked)}
              disabled={isRequired}
              size="small"
            />
            {enabled ? (
              <Tag color="green">Активний</Tag>
            ) : (
              <Tag color="default">Вимкнений</Tag>
            )}
            {isRequired && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Обов'язковий
              </Text>
            )}
          </Space>
        );
      }
    }
  ];

  if (!templateDynamicFields?.endpoints || templateDynamicFields.endpoints.length === 0) {
    return (
      <Card title="Налаштування ендпоінтів">
        <Text type="secondary">
          Обраний шаблон не має ендпоінтів для налаштування
        </Text>
      </Card>
    );
  }

  const expandedRowRender = (record: EndpointMapping) => {
    if (!record.isOffer && !record.hasOfferMacros) {
      return (
        <div style={{ padding: '8px 16px', color: '#666' }}>
          <Text type="secondary">Цей ендпоінт не є offer функцією</Text>
        </div>
      );
    }

    const recordIndex = mappings.findIndex(m => m.handler === record.handler);
    const current = mappings[recordIndex] ?? record;

    return (
      <div style={{ padding: "8px 16px" }}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <Text strong>🎯 Налаштування Offer відповідей</Text>
            <Text
              type="secondary"
              style={{ display: "block", marginTop: "4px" }}
            >
              Конфігурація відповідей для успішних та помилкових результатів
            </Text>
          </div>

          <div style={{ width: "100%" }}>
            {/* Успішна відповідь */}
            <Card
              size="small"
              title={
                <Text strong style={{ color: "#52c41a" }}>
                  ✅ Успішна відповідь
                </Text>
              }
              style={{ marginBottom: "16px" }}
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <div>
                    <Text strong>Тип відповіді:</Text>
                    <Select
                      value={current.offerResponseType || "redirect"}
                      onChange={(value) =>
                        updateMapping(recordIndex, "offerResponseType", value)
                      }
                      style={{ width: "100%", marginTop: "4px" }}
                    >
                      <Option value="redirect">Redirect (302)</Option>
                      <Option value="json">JSON Response</Option>
                      <Option value="websocket">WebSocket</Option>
                      <Option
                        value="encrypted"
                        disabled={!encryptionKeyMacroExists}
                        title={!encryptionKeyMacroExists ? "У шаблоні відсутній макрос ENCRYPTION_KEY" : undefined}
                      >
                        Encrypted {!encryptionKeyMacroExists && "(вимагає макрос ENCRYPTION_KEY у шаблоні)"}
                      </Option>
                    </Select>
                    {!hasEncryptionKey && current.offerResponseType === "encrypted" && (
                      <Alert
                        message="ENCRYPTION_KEY не заповнений"
                        description="Вкажіть значення ENCRYPTION_KEY у динамічних полях — інакше зашифровані відповіді повертатимуть помилку (ключ має бути рівно 32 символи)."
                        type="warning"
                        showIcon
                        style={{ marginTop: "8px" }}
                      />
                    )}
                  </div>
                </Col>

                {current.offerResponseType === "json" && (
                  <Col span={8}>
                    <div>
                      <Text strong>HTTP код:</Text>
                      <Select
                        value={current.offerResponseCode || 200}
                        onChange={(value) =>
                          updateMapping(recordIndex, "offerResponseCode", value)
                        }
                        style={{ width: "100%", marginTop: "4px" }}
                      >
                        <Option value={200}>200 OK</Option>
                        <Option value={201}>201 Created</Option>
                        <Option value={202}>202 Accepted</Option>
                        <Option value={400}>400 Bad Request</Option>
                        <Option value={404}>404 Not Found</Option>
                        <Option value={500}>500 Server Error</Option>
                      </Select>
                    </div>
                  </Col>
                )}
              </Row>

              {current.offerResponseType === "json" && (
                <div style={{ marginTop: "12px" }}>
                  <Text strong>
                    JSON відповідь з макросом {"{{OFFER_URL}}"} та {"{{TIMESTAMP}}"} (час в UTC):{" "}
                  </Text>
                  <div
                    style={{
                      marginTop: "8px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                    }}
                  >
                    <Editor
                      key={`offerUrl-${recordIndex}`}
                      height="120px"
                      language="json"
                      theme={monacoTheme}
                      value={
                        monacoLocalValues.current[`offerUrl-${recordIndex}`] !== undefined
                          ? monacoLocalValues.current[`offerUrl-${recordIndex}`]
                          : (current.offerUrl || "")
                      }
                      onChange={(value) => {
                        // Оновлюємо тільки локальний стан - не викликаємо updateMapping
                        const editorKey = `offerUrl-${recordIndex}`;
                        monacoLocalValues.current[editorKey] = value || "";
                      }}
                      onMount={(editor) => {
                        const editorKey = `offerUrl-${recordIndex}`;
                        monacoEditorRefs.current[editorKey] = editor;
                        // Ініціалізуємо локальне значення
                        if (monacoLocalValues.current[editorKey] === undefined) {
                          monacoLocalValues.current[editorKey] = current.offerUrl || "";
                        }
                        // Додаємо обробник втрати фокусу
                        editor.onDidBlurEditorText(() => {
                          const localValue = monacoLocalValues.current[editorKey] || "";
                          // Синхронізуємо з основним станом тільки при втраті фокусу
                          updateMapping(recordIndex, "offerUrl", localValue);
                        });
                      }}
                      options={{
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        fontSize: 12,
                        lineNumbers: "off",
                        automaticLayout: true,
                        renderLineHighlight: "none",
                        wordWrap: "on",
                        folding: false,
                        glyphMargin: false,
                        lineDecorationsWidth: 0,
                        lineNumbersMinChars: 0,
                        overviewRulerBorder: false,
                        overviewRulerLanes: 0,
                        hideCursorInOverviewRuler: true,
                        scrollbar: {
                          vertical: "hidden",
                          horizontal: "hidden",
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              {current.offerResponseType === "redirect" && (
                <div style={{ marginTop: "12px" }}>
                  <Alert
                    message="Redirect налаштування"
                    description="URL офера буде автоматично отриманий з результату виконання функції. HTTP код завжди 302."
                    type="info"
                    showIcon
                  />
                </div>
              )}

              {current.offerResponseType === "encrypted" && (
                <>
                  <Row gutter={[16, 16]} style={{ marginTop: "12px" }}>
                    <Col span={12}>
                      <div>
                        <Text strong>Алгоритм шифрування:</Text>
                        <Select
                          value={current.encryptionAlgorithm || "aes-256-cbc"}
                          onChange={(value) =>
                            updateMapping(recordIndex, "encryptionAlgorithm", value)
                          }
                          style={{ width: "100%", marginTop: "4px" }}
                        >
                          <Option value="aes-256-cbc">AES-256-CBC (iOS)</Option>
                          <Option value="aes-256-gcm">AES-256-GCM (AEAD)</Option>
                        </Select>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div>
                        <Text strong>HTTP код:</Text>
                        <Select
                          value={current.offerResponseCode || 200}
                          onChange={(value) =>
                            updateMapping(recordIndex, "offerResponseCode", value)
                          }
                          style={{ width: "100%", marginTop: "4px" }}
                        >
                          <Option value={200}>200 OK</Option>
                          <Option value={201}>201 Created</Option>
                          <Option value={202}>202 Accepted</Option>
                          <Option value={400}>400 Bad Request</Option>
                          <Option value={404}>404 Not Found</Option>
                          <Option value={500}>500 Server Error</Option>
                        </Select>
                      </div>
                    </Col>
                  </Row>

                  <div style={{ marginTop: "12px" }}>
                    <Text strong>
                      JSON Template з макросами {"{{OFFER_URL}}"}, {"{{TIMESTAMP}}"} та іншими макросами з динамічних полів:
                    </Text>
                    <div
                      style={{
                        marginTop: "8px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                      }}
                    >
                      <Editor
                        key={`offerUrl-encrypted-${recordIndex}`}
                        height="150px"
                        language="json"
                        theme={monacoTheme}
                        value={
                          monacoLocalValues.current[`offerUrl-encrypted-${recordIndex}`] !== undefined
                            ? monacoLocalValues.current[`offerUrl-encrypted-${recordIndex}`]
                            : (current.offerUrl || "")
                        }
                        onChange={(value) => {
                          const editorKey = `offerUrl-encrypted-${recordIndex}`;
                          monacoLocalValues.current[editorKey] = value || "";
                        }}
                        onMount={(editor) => {
                          const editorKey = `offerUrl-encrypted-${recordIndex}`;
                          monacoEditorRefs.current[editorKey] = editor;
                          if (monacoLocalValues.current[editorKey] === undefined) {
                            monacoLocalValues.current[editorKey] = current.offerUrl || "";
                          }
                          editor.onDidBlurEditorText(() => {
                            const localValue = monacoLocalValues.current[editorKey] || "";
                            updateMapping(recordIndex, "offerUrl", localValue);
                          });
                        }}
                        options={{
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 12,
                          lineNumbers: "off",
                          automaticLayout: true,
                          renderLineHighlight: "none",
                          wordWrap: "on",
                          folding: false,
                          glyphMargin: false,
                          lineDecorationsWidth: 0,
                          lineNumbersMinChars: 0,
                          overviewRulerBorder: false,
                          overviewRulerLanes: 0,
                          hideCursorInOverviewRuler: true,
                          scrollbar: {
                            vertical: "hidden",
                            horizontal: "hidden",
                          },
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              {current.offerResponseType === "websocket" && (
                <>
                  <div style={{ marginTop: "12px" }}>
                    <Alert
                      message="WebSocket налаштування"
                      description="Додаток відкриє WebSocket-з'єднання за цим шляхом, надішле JSON payload і отримає Gray/White JSON у відповідь. Метод завжди GET (WebSocket upgrade)."
                      type="info"
                      showIcon
                    />
                  </div>
                  <div style={{ marginTop: "12px" }}>
                    <Text strong>
                      Gray Result JSON з макросом {"{{OFFER_URL}}"}:
                    </Text>
                    <div
                      style={{
                        marginTop: "8px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                      }}
                    >
                      <Editor
                        key={`offerUrl-websocket-${recordIndex}`}
                        height="150px"
                        language="json"
                        theme={monacoTheme}
                        value={
                          monacoLocalValues.current[`offerUrl-websocket-${recordIndex}`] !== undefined
                            ? monacoLocalValues.current[`offerUrl-websocket-${recordIndex}`]
                            : (current.offerUrl || "")
                        }
                        onChange={(value) => {
                          const editorKey = `offerUrl-websocket-${recordIndex}`;
                          monacoLocalValues.current[editorKey] = value || "";
                        }}
                        onMount={(editor) => {
                          const editorKey = `offerUrl-websocket-${recordIndex}`;
                          monacoEditorRefs.current[editorKey] = editor;
                          if (monacoLocalValues.current[editorKey] === undefined) {
                            monacoLocalValues.current[editorKey] = current.offerUrl || "";
                          }
                          editor.onDidBlurEditorText(() => {
                            const localValue = monacoLocalValues.current[editorKey] || "";
                            updateMapping(recordIndex, "offerUrl", localValue);
                          });
                        }}
                        options={{
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 12,
                          lineNumbers: "off",
                          automaticLayout: true,
                          renderLineHighlight: "none",
                          wordWrap: "on",
                          folding: false,
                          glyphMargin: false,
                          lineDecorationsWidth: 0,
                          lineNumbersMinChars: 0,
                          overviewRulerBorder: false,
                          overviewRulerLanes: 0,
                          hideCursorInOverviewRuler: true,
                          scrollbar: {
                            vertical: "hidden",
                            horizontal: "hidden",
                          },
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </Card>

            {/* Помилкова відповідь */}
            <Card
              size="small"
              title={
                <Text strong style={{ color: "#ff4d4f" }}>
                  ❌ Помилкова відповідь
                </Text>
              }
            >
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <div>
                    <Text strong>Тип відповіді:</Text>
                    <Select
                      value={current.offerErrorType || "redirect"}
                      onChange={(value) =>
                        updateMapping(recordIndex, "offerErrorType", value)
                      }
                      style={{ width: "100%", marginTop: "4px" }}
                    >
                      <Option value="redirect">Redirect (302)</Option>
                      <Option value="json">JSON Response</Option>
                      <Option value="render">Landing (HTML)</Option>
                      <Option
                        value="encrypted"
                        disabled={!encryptionKeyMacroExists}
                        title={!encryptionKeyMacroExists ? "У шаблоні відсутній макрос ENCRYPTION_KEY" : undefined}
                      >
                        Encrypted {!encryptionKeyMacroExists && "(вимагає макрос ENCRYPTION_KEY у шаблоні)"}
                      </Option>
                    </Select>
                    {!hasEncryptionKey && current.offerErrorType === "encrypted" && (
                      <Alert
                        message="ENCRYPTION_KEY не заповнений"
                        description="Вкажіть значення ENCRYPTION_KEY у динамічних полях — інакше зашифровані відповіді повертатимуть помилку (ключ має бути рівно 32 символи)."
                        type="warning"
                        showIcon
                        style={{ marginTop: "8px" }}
                      />
                    )}
                  </div>
                </Col>

                {(current.offerErrorType === "json" || current.offerErrorType === "encrypted") && (
                  <Col span={8}>
                    <div>
                      <Text strong>HTTP код:</Text>
                      <Select
                        value={current.offerErrorCode || 400}
                        onChange={(value) =>
                          updateMapping(recordIndex, "offerErrorCode", value)
                        }
                        style={{ width: "100%", marginTop: "4px" }}
                      >
                        <Option value={200}>200 OK</Option>
                        <Option value={400}>400 Bad Request</Option>
                        <Option value={404}>404 Not Found</Option>
                        <Option value={422}>422 Unprocessable Entity</Option>
                        <Option value={500}>500 Server Error</Option>
                        <Option value={503}>503 Service Unavailable</Option>
                      </Select>
                    </div>
                  </Col>
                )}
              </Row>

              <div style={{ marginTop: "12px" }}>
                {current.offerErrorType === "redirect" && (
                  <div>
                    <Text strong>URL для перенаправлення:</Text>
                    <Input
                      value={current.offerError || ""}
                      onChange={(e) =>
                        updateMapping(recordIndex, "offerError", e.target.value)
                      }
                      placeholder="https://example.com/error"
                      style={{ marginTop: "8px" }}
                      addonAfter={
                        <Tooltip title="Згенерувати приклад">
                          <Button
                            icon={<ReloadOutlined />}
                            type="text"
                            size="small"
                            onClick={() =>
                              updateMapping(
                                recordIndex,
                                "offerError",
                                generateRandomUrl("error")
                              )
                            }
                          />
                        </Tooltip>
                      }
                    />
                  </div>
                )}

                {current.offerErrorType === "json" && (
                  <div>
                    <Text strong>
                      {current.offerResponseType === "websocket"
                        ? "White Result JSON з макросом {{OFFER_ERROR}}:"
                        : "JSON відповідь з макросом {{OFFER_ERROR}} та {{TIMESTAMP}} (час в UTC):"}{" "}
                    </Text>
                    <div
                      style={{
                        marginTop: "8px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "6px",
                      }}
                    >
                      <Editor
                        key={`offerError-${recordIndex}`}
                        height="120px"
                        language="json"
                        theme={monacoTheme}
                        value={
                          monacoLocalValues.current[`offerError-${recordIndex}`] !== undefined
                            ? monacoLocalValues.current[`offerError-${recordIndex}`]
                            : (current.offerError || "")
                        }
                        onChange={(value) => {
                          // Оновлюємо тільки локальний стан - не викликаємо updateMapping
                          const editorKey = `offerError-${recordIndex}`;
                          monacoLocalValues.current[editorKey] = value || "";
                        }}
                        onMount={(editor) => {
                          const editorKey = `offerError-${recordIndex}`;
                          monacoEditorRefs.current[editorKey] = editor;
                          // Ініціалізуємо локальне значення
                          if (monacoLocalValues.current[editorKey] === undefined) {
                            monacoLocalValues.current[editorKey] = current.offerError || "";
                          }
                          // Додаємо обробник втрати фокусу
                          editor.onDidBlurEditorText(() => {
                            const localValue = monacoLocalValues.current[editorKey] || "";
                            // Синхронізуємо з основним станом тільки при втраті фокусу
                            updateMapping(recordIndex, "offerError", localValue);
                          });
                        }}
                        options={{
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 12,
                          lineNumbers: "off",
                          automaticLayout: true,
                          renderLineHighlight: "none",
                          wordWrap: "on",
                          folding: false,
                          glyphMargin: false,
                          lineDecorationsWidth: 0,
                          lineNumbersMinChars: 0,
                          overviewRulerBorder: false,
                          overviewRulerLanes: 0,
                          hideCursorInOverviewRuler: true,
                          scrollbar: {
                            vertical: "hidden",
                            horizontal: "hidden",
                          },
                        }}
                      />
                    </div>
                  </div>
                )}

                {current.offerErrorType === "encrypted" && (
                  <>
                    <Row gutter={[16, 16]} style={{ marginTop: "12px" }}>
                      <Col span={12}>
                        <div>
                          <Text strong>Алгоритм шифрування:</Text>
                          <Select
                            value={current.encryptionAlgorithm || "aes-256-cbc"}
                            onChange={(value) =>
                              updateMapping(recordIndex, "encryptionAlgorithm", value)
                            }
                            style={{ width: "100%", marginTop: "4px" }}
                          >
                            <Option value="aes-256-cbc">AES-256-CBC (iOS)</Option>
                            <Option value="aes-256-gcm">AES-256-GCM (AEAD)</Option>
                          </Select>
                        </div>
                      </Col>
                      <Col span={12}>
                        <div>
                          <Text strong>HTTP код:</Text>
                          <Select
                            value={current.offerErrorCode || 400}
                            onChange={(value) =>
                              updateMapping(recordIndex, "offerErrorCode", value)
                            }
                            style={{ width: "100%", marginTop: "4px" }}
                          >
                            <Option value={200}>200 OK</Option>
                            <Option value={400}>400 Bad Request</Option>
                            <Option value={404}>404 Not Found</Option>
                            <Option value={422}>422 Unprocessable Entity</Option>
                            <Option value={500}>500 Server Error</Option>
                            <Option value={503}>503 Service Unavailable</Option>
                          </Select>
                        </div>
                      </Col>
                    </Row>

                    <div style={{ marginTop: "12px" }}>
                      <Alert
                        message="Ключ шифрування"
                        description="Використовується значення з макросу ENCRYPTION_KEY (32 символи для AES-256-CBC). Один ключ використовується для iOS та Android."
                        type="info"
                        showIcon
                        style={{ marginBottom: "12px" }}
                      />
                    </div>

                    <div style={{ marginTop: "12px" }}>
                      <Text strong>
                        JSON Template з макросами {"{{OFFER_ERROR}}"}, {"{{TIMESTAMP}}"} та іншими макросами з динамічних полів:
                      </Text>
                      <div
                        style={{
                          marginTop: "8px",
                          border: "1px solid #d9d9d9",
                          borderRadius: "6px",
                        }}
                      >
                        <Editor
                          key={`offerError-encrypted-${recordIndex}`}
                          height="200px"
                          language="json"
                          theme={monacoTheme}
                          value={
                            monacoLocalValues.current[`offerError-encrypted-${recordIndex}`] !== undefined
                              ? monacoLocalValues.current[`offerError-encrypted-${recordIndex}`]
                              : (current.offerError || "")
                          }
                          onChange={(value) => {
                            const editorKey = `offerError-encrypted-${recordIndex}`;
                            monacoLocalValues.current[editorKey] = value || "";
                          }}
                          onMount={(editor) => {
                            const editorKey = `offerError-encrypted-${recordIndex}`;
                            monacoEditorRefs.current[editorKey] = editor;
                            if (monacoLocalValues.current[editorKey] === undefined) {
                              monacoLocalValues.current[editorKey] = current.offerError || "";
                            }
                            editor.onDidBlurEditorText(() => {
                              const localValue = monacoLocalValues.current[editorKey] || "";
                              updateMapping(recordIndex, "offerError", localValue);
                            });
                          }}
                          options={{
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            fontSize: 12,
                            lineNumbers: "off",
                            automaticLayout: true,
                            renderLineHighlight: "none",
                            wordWrap: "on",
                            folding: false,
                            glyphMargin: false,
                            lineDecorationsWidth: 0,
                            lineNumbersMinChars: 0,
                            overviewRulerBorder: false,
                            overviewRulerLanes: 0,
                            hideCursorInOverviewRuler: true,
                            scrollbar: {
                              vertical: "hidden",
                              horizontal: "hidden",
                            },
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}

                {current.offerErrorType === "render" && (
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Text strong>Лендінг (static site):</Text>
                      <Select
                        key={`site-select-${current.handler}`}
                        style={{ width: "100%", marginTop: 8 }}
                        value={current.errorStaticSiteId || undefined}
                        onChange={(v) =>
                          updateMapping(recordIndex, "errorStaticSiteId", v)
                        }
                        optionLabelProp="label"
                        optionFilterProp="label"
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label as string)
                            ?.toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {staticSites.map((s) => (
                          <Option
                            key={s.id}
                            value={s.id}
                            label={`${s.siteName}`}
                          >
                            <Space direction="vertical" size={0}>
                              <span>
                                <b>{s.siteName}</b>
                              </span>
                              <span style={{ color: "#666" }}>
                                {s.template?.name
                                  ? `Шаблон: ${s.template.name} • `
                                  : ""}
                                {s.subdomain ? `sub: ${s.subdomain} • ` : ""}
                                {s.status ? `status: ${s.status}` : ""}
                              </span>
                            </Space>
                          </Option>
                        ))}
                      </Select>
                    </Col>

                    <Col span={12}>
                      <Text strong>Файл (у пакеті лендінгу):</Text>
                      <Input
                        style={{ marginTop: 8 }}
                        value="index.html"
                        disabled
                        addonBefore="/"
                      />
                      <div
                        style={{ color: "#999", fontSize: 12, marginTop: 4 }}
                      >
                        Зазвичай <code>index.html</code>.
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            </Card>
          </div>
        </Space>
      </div>
    );
  };

  return (
    <Card
      title={
        <Space>
          <SettingOutlined />
          Налаштування ендпоінтів
          <Text type="secondary">({mappings.filter(m => m.enabled).length} активних)</Text>
        </Space>
      }
      size="small"
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Text type="secondary">
          Налаштуйте шляхи, методи та відповіді для ендпоінтів шаблону. Вимкнені ендпоінти не будуть доступні в Worker.
        </Text>

        <Table
          dataSource={mappings}
          columns={columns}
          pagination={false}
          size="small"
          rowKey="handler"
          scroll={{ x: 800 }}
          expandable={{
            expandedRowRender,
            expandRowByClick: false,
            rowExpandable: (record) => !!(record.isOffer || record.hasOfferMacros),
            expandIcon: ({ expanded, onExpand, record }) =>
              (record.isOffer || record.hasOfferMacros) ? (
                <Tooltip title={expanded ? "Сховати налаштування відповідей" : "Показати налаштування відповідей"}>
                  <SettingOutlined
                    onClick={e => onExpand(record, e)}
                    style={{
                      color: expanded ? '#1890ff' : '#666',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  />
                </Tooltip>
              ) : <span style={{ width: '14px', display: 'inline-block' }} />
          }}
        />

        <div style={{ background: '#f5f5f5', padding: '12px', borderRadius: '6px' }}>
          <Text strong>Підказка:</Text>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>Обов'язкові ендпоінти не можна вимкнути</li>
            <li>Переконайтеся що шляхи не конфліктують між собою</li>
            <li>Натисніть на <SettingOutlined /> щоб налаштувати відповіді для ендпоінтів</li>
            <li>Зміни застосуються після деплою застосунку</li>
          </ul>
        </div>
      </Space>
    </Card>
  );
});
EndpointMappingsComponent.displayName = 'EndpointMappingsComponent'; 